import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import DiploksContextHint from "../../components/DiploksContextHint";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { motion } from "framer-motion";
import { Radio, Gavel, Flag, Users } from "lucide-react";

export default function ChairDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/events").then(async (r) => {
      setEvents(r.data);
      const all = [];
      for (const ev of r.data) {
        const s = await api.get(`/sessions?event_id=${ev.id}`);
        s.data.forEach((sess) => all.push({ ...sess, eventTitle: ev.title }));
      }
      setSessions(all);
    });
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Chair / Dais Console
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Welcome to the bench, {user?.name?.split(" ")[0]}.
          </h1>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile icon={Radio} label="Sessions" value={sessions.length} />
          <Tile icon={Gavel} label="Motions" value="—" />
          <Tile icon={Flag} label="Events" value={events.length} />
          <Tile icon={Users} label="Delegates" value="—" />
        </div>

        {/* Chair-side Diploks context — premium reports use organiser Diploks. */}
        <div className="mt-6">
          <DiploksContextHint
            context="premium chair reports and feedback summaries"
            testId="chair-diploks-context-hint"
          />
        </div>

        <div className="mt-10">
          <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">Live sessions</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {sessions.map((s) => (
              <Link to={`/dashboard/live/${s.id}`} key={s.id} className="rounded-xl bg-[#0A192F] text-white p-6 border border-[#D4AF37]/20 hover:-translate-y-1 transition-all block" data-testid={`chair-session-${s.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                    {s.eventTitle}
                  </p>
                  <span className="flex items-center gap-1 text-xs font-mono">
                    <Radio className="h-3 w-3 text-emerald-400" /> {s.status}
                  </span>
                </div>
                <h3 className="mt-3 text-lg font-semibold">{s.agenda}</h3>
                <p className="mt-2 text-xs text-slate-400">Open session floor →</p>
              </Link>
            ))}
            {sessions.length === 0 && (
              <Card className="bg-white border-dashed border-2 border-slate-200 col-span-2">
                <CardContent className="p-10 text-center">
                  <p className="text-sm text-slate-500">No sessions running right now.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1 font-serif text-3xl text-[#0A192F]">{value}</p>
    </div>
  );
}
